import { useState, useEffect, useCallback } from "react";
import { PlayerRole, COMM_LEVEL_MIN, COMM_LEVEL_MAX, SCAN_LEVEL_MIN, SCAN_LEVEL_MAX, MAX_ALLOWED_SPACESHIP_DAMAGE, MAX_ACTIONS_PER_TURN, MAX_LANDING_SITE_POINTS, MAX_CHARS_SENT_PER_TURN } from "../shared/constants";
import { authenticatedFetch, API_BASE_URL } from "../utils/api";
import AdminCounter from "./AdminCounter";
import ActionModal from "./ActionModal";
import AdminMessageModal from "./AdminMessageModal";
import { buildPlayerName } from "../shared/helper";

interface Player {
  player_id: string;
  team_id: string;
  login_code: string;
  role: string;
  comm_lvl: number | null;
  scan_lvl: number | null;
  ship_dmg?: number | null;
  shields_on?: boolean;
  landing_gear?: string;
  personal_score?: number | null;
  team_name: string;
  team_code_name: string;
  game_name: string;
  action_count?: number | null;
  landing_site_points?: number;
  landing_site_investments?: Array<{player_id: string; player_name: string; team_name: string; count: number}>;
}

interface PlayerCardProps {
  player: Player;
  gameStatus?: string;
  currentTurn?: number;
  actionCount?: number | null; // Passed from parent (batch fetched)
  charsSentThisTurn?: number | null; // Passed from parent (batch fetched)
  landingSiteInvestmentData?: { total_points: number; investments: Array<{player_id: string; player_name: string; team_name: string; count: number}> } | null; // Passed from parent (batch fetched)
}

export default function PlayerCard({ player, gameStatus, currentTurn, actionCount: actionCountProp, charsSentThisTurn: charsSentThisTurnProp, landingSiteInvestmentData: landingSiteInvestmentDataProp }: PlayerCardProps) {
  const [commLevel, setCommLevel] = useState<number | null>(
    typeof player.comm_lvl === "number" ? player.comm_lvl : null
  );
  const [scanLevel, setScanLevel] = useState<number | null>(
    typeof player.scan_lvl === "number" ? player.scan_lvl : null
  );
  const [actionCount, setActionCount] = useState<number | null>(
    actionCountProp !== undefined ? actionCountProp : (typeof player.action_count === "number" ? player.action_count : null)
  );
  const [personalScore, setPersonalScore] = useState<number>(
    typeof player.personal_score === "number" ? player.personal_score : 0
  );
  const [isLandingSiteModalOpen, setIsLandingSiteModalOpen] = useState<boolean>(false);
  const [landingSiteInvestments, setLandingSiteInvestments] = useState<Array<{player_id: string; player_name: string; team_name: string; count: number}>>([]);
  const [showAdminMessageModal, setShowAdminMessageModal] = useState<boolean>(false);
  const [isLoadingInvestments, setIsLoadingInvestments] = useState<boolean>(false);
  const [totalLandingSitePoints, setTotalLandingSitePoints] = useState<number>(0);
  const [charsSentThisTurn, setCharsSentThisTurn] = useState<number>(
    (charsSentThisTurnProp !== undefined && charsSentThisTurnProp !== null) ? charsSentThisTurnProp : 0
  );

  const teamName = player.team_name;
  const roleValue = player.role; // "Agency" or "Spaceship"
  const displayName = buildPlayerName(player.team_name, roleValue);
  const isGameActive = (gameStatus ?? "").toUpperCase() === "ACTIVE";

  // Sync local mirrored state when the player prop changes
  useEffect(() => {
    setCommLevel(typeof player.comm_lvl === "number" ? player.comm_lvl : null);
    setScanLevel(typeof player.scan_lvl === "number" ? player.scan_lvl : null);
    setDamage(typeof player.ship_dmg === "number" ? player.ship_dmg : 0);
    setShieldsOn(Boolean(player.shields_on));
    setPersonalScore(typeof player.personal_score === "number" ? player.personal_score : 0);
    if (typeof player.action_count === "number") {
      setActionCount(player.action_count);
    }
    // Update landing site points if provided in player data
    if (typeof player.landing_site_points === "number") {
      setTotalLandingSitePoints(player.landing_site_points);
    }
    // Update landing site investments if provided in player data
    if (Array.isArray(player.landing_site_investments)) {
      setLandingSiteInvestments(player.landing_site_investments);
    }
  }, [player]);

  // Cache utilities for action count (similar to character count)
  const ACTION_COUNT_CACHE_KEY = (playerId: string, turn: number | null | undefined) => 
    `lost-in-space-action-count-${playerId}-${turn ?? 'null'}`;

  const getCachedActionCount = useCallback((playerId: string, turn: number | null | undefined): number | null => {
    if (turn === null || turn === undefined) return null;
    try {
      const cached = localStorage.getItem(ACTION_COUNT_CACHE_KEY(playerId, turn));
      if (!cached) return null;
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      // Cache expires after 2 minutes (same as other caches, action count resets each turn anyway)
      if (now - timestamp > 2 * 60 * 1000) {
        localStorage.removeItem(ACTION_COUNT_CACHE_KEY(playerId, turn));
        return null;
      }
      return typeof data === 'number' ? data : null;
    } catch (err) {
      console.error("Error reading action count cache:", err);
      localStorage.removeItem(ACTION_COUNT_CACHE_KEY(playerId, turn));
      return null;
    }
  }, []);

  const setCachedActionCount = useCallback((playerId: string, turn: number | null | undefined, count: number) => {
    if (turn === null || turn === undefined) return;
    try {
      const cacheData = {
        data: count,
        timestamp: Date.now()
      };
      localStorage.setItem(ACTION_COUNT_CACHE_KEY(playerId, turn), JSON.stringify(cacheData));
    } catch (err) {
      console.error("Error writing action count cache:", err);
    }
  }, []);

  // Update action count from prop if provided, otherwise fetch individually (with caching)
  useEffect(() => {
    if (actionCountProp !== undefined && actionCountProp !== null) {
      setActionCount(actionCountProp);
      // Update cache with prop value if available
      if (currentTurn !== null && currentTurn !== undefined) {
        setCachedActionCount(player.player_id, currentTurn, actionCountProp);
      }
    } else if (currentTurn !== null && currentTurn !== undefined) {
      // Check cache first if prop not provided
      const cachedCount = getCachedActionCount(player.player_id, currentTurn);
      if (cachedCount !== null) {
        console.log("Using cached action count");
        setActionCount(cachedCount);
        return;
      }

      // Only fetch if not provided via prop and cache is empty
      // Action count resets each turn, so we need to fetch for new turn
      const fetchActionCount = async () => {
        try {
          const res = await authenticatedFetch(
            `/players/action-count?player_id=${encodeURIComponent(player.player_id)}`
          );
          if (res.ok) {
            const data = await res.json();
            const count = data.action_count ?? 0;
            setActionCount(count);
            // Update cache
            setCachedActionCount(player.player_id, currentTurn, count);
          }
        } catch (error) {
          console.error("Failed to fetch action count:", error);
          setActionCount(0);
        }
      };
      fetchActionCount();
    } else {
      setActionCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.player_id, currentTurn, actionCountProp, getCachedActionCount, setCachedActionCount]);

  // Cache utilities for character count
  const CHAR_COUNT_CACHE_KEY = (playerId: string, turn: number | null | undefined) => 
    `lost-in-space-char-count-${playerId}-${turn ?? 'null'}`;

  const getCachedCharCount = useCallback((playerId: string, turn: number | null | undefined): number | null => {
    if (turn === null || turn === undefined) return null;
    try {
      const cached = localStorage.getItem(CHAR_COUNT_CACHE_KEY(playerId, turn));
      if (!cached) return null;
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      // Cache expires after 2 minutes (same as player data cache)
      if (now - timestamp > 2 * 60 * 1000) {
        localStorage.removeItem(CHAR_COUNT_CACHE_KEY(playerId, turn));
        return null;
      }
      return typeof data === 'number' ? data : null;
    } catch (err) {
      console.error("Error reading character count cache:", err);
      localStorage.removeItem(CHAR_COUNT_CACHE_KEY(playerId, turn));
      return null;
    }
  }, []);

  const setCachedCharCount = useCallback((playerId: string, turn: number | null | undefined, count: number) => {
    if (turn === null || turn === undefined) return;
    try {
      const cacheData = {
        data: count,
        timestamp: Date.now()
      };
      localStorage.setItem(CHAR_COUNT_CACHE_KEY(playerId, turn), JSON.stringify(cacheData));
    } catch (err) {
      console.error("Error writing character count cache:", err);
    }
  }, []);

  // Update chars sent from prop if provided, otherwise fetch individually (with caching)
  useEffect(() => {
    if (charsSentThisTurnProp !== undefined && charsSentThisTurnProp !== null) {
      setCharsSentThisTurn(charsSentThisTurnProp);
      // Update cache with prop value if available
      if (currentTurn !== null && currentTurn !== undefined) {
        setCachedCharCount(player.player_id, currentTurn, charsSentThisTurnProp);
      }
    } else if (currentTurn !== null && currentTurn !== undefined) {
      // Check cache first
      const cachedCount = getCachedCharCount(player.player_id, currentTurn);
      if (cachedCount !== null) {
        console.log("Using cached character count");
        setCharsSentThisTurn(cachedCount);
        return;
      }

      // Only fetch if not provided via prop and cache is empty
      const fetchCharsSent = async () => {
        try {
          const res = await authenticatedFetch(
            `/messages/chars-sent-this-turn?player_id=${encodeURIComponent(player.player_id)}`
          );
          if (res.ok) {
            const data = await res.json();
            const count = data.chars_sent ?? 0;
            setCharsSentThisTurn(count);
            // Update cache
            if (currentTurn !== null && currentTurn !== undefined) {
              setCachedCharCount(player.player_id, currentTurn, count);
            }
          }
        } catch (error) {
          console.error("Failed to fetch characters sent:", error);
          setCharsSentThisTurn(0);
        }
      };
      fetchCharsSent();
    } else {
      setCharsSentThisTurn(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.player_id, currentTurn, charsSentThisTurnProp, getCachedCharCount, setCachedCharCount]);

  // Listen for action count updates from WebSocket or custom events
  useEffect(() => {
    const handleActionCountUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      const detail = customEvent.detail;
      
      // Check if this update is relevant to this player
      const isRelevant = detail?.player_id === player.player_id || detail?.message?.player_id === player.player_id;
      
      if (!isRelevant) return;

      // Use action count from event if available (no refetch needed)
      if (typeof detail?.action_count === 'number') {
        console.log("Updating action count from event:", detail.action_count);
        setActionCount(detail.action_count);
        // Update cache
        if (currentTurn !== null && currentTurn !== undefined) {
          setCachedActionCount(player.player_id, currentTurn, detail.action_count);
        }
      }
    };

    window.addEventListener('actionCountUpdate', handleActionCountUpdate);
    return () => {
      window.removeEventListener('actionCountUpdate', handleActionCountUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.player_id, currentTurn, setCachedActionCount]);

  // Listen for new messages to update character count from WebSocket (no refetch needed)
  useEffect(() => {
    const handleNewMessage = (event: Event) => {
      const customEvent = event as CustomEvent;
      const detail = customEvent.detail;
      
      // Check if this message is relevant to this player
      const isRelevant = detail?.message?.sending_player_id === player.player_id || 
                        detail?.message?.receiving_player_id === player.player_id;
      
      if (!isRelevant) return;

      // Use character count from WebSocket event if available (no refetch needed)
      if (typeof detail?.chars_sent_this_turn === 'number') {
        console.log("Updating character count from WebSocket:", detail.chars_sent_this_turn);
        setCharsSentThisTurn(detail.chars_sent_this_turn);
        // Update cache
        if (currentTurn !== null && currentTurn !== undefined) {
          setCachedCharCount(player.player_id, currentTurn, detail.chars_sent_this_turn);
        }
        return;
      }
      
      // Fallback: only fetch if WebSocket didn't provide the count (old message format)
      if (charsSentThisTurnProp === undefined) {
        const fetchCharsSent = async () => {
          try {
            const res = await authenticatedFetch(
              `/messages/chars-sent-this-turn?player_id=${encodeURIComponent(player.player_id)}`
            );
            if (res.ok) {
              const data = await res.json();
              const count = data.chars_sent ?? 0;
              setCharsSentThisTurn(count);
              // Update cache
              if (currentTurn !== null && currentTurn !== undefined) {
                setCachedCharCount(player.player_id, currentTurn, count);
              }
            }
          } catch (error) {
            console.error("Failed to refresh characters sent:", error);
          }
        };
        fetchCharsSent();
      }
    };

    window.addEventListener('newMessage', handleNewMessage);
    return () => {
      window.removeEventListener('newMessage', handleNewMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.player_id, currentTurn, charsSentThisTurnProp, setCachedCharCount]);

  // Fetch landing site investments for agency players (only if not provided via prop)
  useEffect(() => {
    if (player.role !== PlayerRole.AGENCY) {
      setTotalLandingSitePoints(0);
      setLandingSiteInvestments([]);
      setIsLoadingInvestments(false);
      return;
    }
    
    // If landing site investment data is provided via prop, use it
    if (landingSiteInvestmentDataProp) {
      setTotalLandingSitePoints(landingSiteInvestmentDataProp.total_points || 0);
      setLandingSiteInvestments(landingSiteInvestmentDataProp.investments || []);
      setIsLoadingInvestments(false);
      return;
    }
    
    // If landing site investments are already provided in player prop, use them
    if (Array.isArray(player.landing_site_investments)) {
      const total = player.landing_site_investments.reduce((sum: number, inv: any) => sum + (inv.count || 0), 0);
      setTotalLandingSitePoints(total);
      setLandingSiteInvestments(player.landing_site_investments);
      setIsLoadingInvestments(false);
      return;
    }
    
    // Otherwise, fetch individually (fallback for standalone usage)
    const fetchInvestments = async () => {
      setIsLoadingInvestments(true);
      try {
        const res = await authenticatedFetch(
          `/players/landing-site-investments?player_id=${encodeURIComponent(player.player_id)}`
        );
        if (res.ok) {
          const data = await res.json();
          const total = Array.isArray(data) ? data.reduce((sum: number, inv: any) => sum + (inv.count || 0), 0) : 0;
          setTotalLandingSitePoints(total);
          setLandingSiteInvestments(data || []);
        } else {
          setTotalLandingSitePoints(0);
          setLandingSiteInvestments([]);
        }
      } catch (error) {
        console.error("Failed to fetch landing site investments:", error);
        setTotalLandingSitePoints(0);
        setLandingSiteInvestments([]);
      } finally {
        setIsLoadingInvestments(false);
      }
    };

    fetchInvestments();
  }, [player.player_id, player.role, player.landing_site_investments, landingSiteInvestmentDataProp]);

  // Helper: call backend to adjust comm level by delta (+1 or -1)
  const updateCommLevel = async (delta: number) => {
    const endpoint = delta > 0 ? "/players/comm/inc" : "/players/comm/dec";
    const body = JSON.stringify({
      team_name: teamName,
      player_role: roleValue
    });
    const res = await authenticatedFetch(endpoint, {
      method: "POST",
      body
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.detail || "Failed to update communication level");
    }
    const data = await res.json();
    setCommLevel(data.comm_lvl ?? null);
  };

  // Helper: call backend to adjust scan level by delta (+1 or -1)
  const updateScanLevel = async (delta: number) => {
    const endpoint = delta > 0 ? "/players/scan/inc" : "/players/scan/dec";
    const body = JSON.stringify({
      team_name: teamName,
      player_role: roleValue
    });
    const res = await authenticatedFetch(endpoint, {
      method: "POST",
      body
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.detail || "Failed to update scanning level");
    }
    const data = await res.json();
    setScanLevel(data.scan_lvl ?? null);
  };

  // When the number input changes via arrows or typing, compute delta and call appropriate endpoint(s)
  const handleCommChange = async (next: number) => {
    const current = commLevel ?? COMM_LEVEL_MIN;
    const bounded = Math.max(COMM_LEVEL_MIN, Math.min(COMM_LEVEL_MAX, next));
    if (bounded === current) return;
    const delta = bounded - current;
    // perform |delta| times due to per-step endpoints
    const steps = Math.abs(delta);
    for (let i = 0; i < steps; i++) {
      await updateCommLevel(delta > 0 ? 1 : -1);
    }
  };

  const handleScanChange = async (next: number) => {
    const current = scanLevel ?? SCAN_LEVEL_MIN;
    const bounded = Math.max(SCAN_LEVEL_MIN, Math.min(SCAN_LEVEL_MAX, next));
    if (bounded === current) return;
    const delta = bounded - current;
    const steps = Math.abs(delta);
    for (let i = 0; i < steps; i++) {
      await updateScanLevel(delta > 0 ? 1 : -1);
    }
  };

  // Spaceship damage/shield update helpers
  const [damage, setDamage] = useState<number>(
    typeof player.ship_dmg === "number" ? player.ship_dmg : 0
  );
  const [shieldsOn, setShieldsOn] = useState<boolean>(Boolean(player.shields_on));
  const isEliminated =
    roleValue === PlayerRole.SPACESHIP &&
    ((typeof damage === "number" ? damage : (player.ship_dmg ?? 0)) >= MAX_ALLOWED_SPACESHIP_DAMAGE);
  const updateDamage = async (delta: number) => {
    const endpoint = delta > 0 ? "/players/damage/inc" : "/players/damage/dec";
    const body = JSON.stringify({
      team_name: teamName,
      player_role: roleValue
    });
    const res = await authenticatedFetch(endpoint, {
      method: "POST",
      body
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.detail || "Failed to update damage");
    }
    const data = await res.json();
    setDamage((data as any).ship_dmg ?? 0);
  };
  const handleDamageChange = async (next: number) => {
    const current = damage ?? 0;
    const bounded = Math.max(0, Math.min(MAX_ALLOWED_SPACESHIP_DAMAGE, next));
    if (bounded === current) return;
    const delta = bounded - current;
    const steps = Math.abs(delta);
    for (let i = 0; i < steps; i++) {
      await updateDamage(delta > 0 ? 1 : -1);
    }
  };

  const handleShieldsToggle = async (nextValue: boolean) => {
    try {
      const endpoint = nextValue ? "/players/shields/on" : "/players/shields/off";
      const res = await authenticatedFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({
          player_id: player.player_id
        })
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || "Failed to update shields");
      }
      const data = await res.json().catch(() => ({}));
      setShieldsOn(typeof data.shields_on === "boolean" ? data.shields_on : nextValue);
    } catch (error) {
      console.error("Failed to update shields:", error);
    }
  };

  const updatePersonalScore = async (delta: number) => {
    const endpoint = delta > 0 ? "/players/victory/inc" : "/players/victory/dec";
    const body = JSON.stringify({
      player_id: player.player_id
    });
    const res = await authenticatedFetch(endpoint, {
      method: "POST",
      body
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.detail || "Failed to update victory points");
    }
    const data = await res.json();
    setPersonalScore((prev) =>
      typeof data.personal_score === "number"
        ? data.personal_score
        : Math.max(0, prev + (delta > 0 ? 1 : -1))
    );
  };

  const handlePersonalScoreChange = async (next: number) => {
    const MAX_VICTORY_POINTS = 999;
    const current = personalScore;
    const bounded = Math.max(0, Math.min(MAX_VICTORY_POINTS, next));
    if (bounded === current) return;
    const delta = bounded - current;
    const steps = Math.abs(delta);
    for (let i = 0; i < steps; i++) {
      await updatePersonalScore(delta > 0 ? 1 : -1);
    }
  };

  return (
    <div style={{
      padding: "0.75rem",
      backgroundColor: "white",
      borderRadius: "6px",
      border: "1px solid #dee2e6",
      marginTop: "0.5rem"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
        <div style={{ flex: 1 }}>
          <strong style={{ color: "#212529", fontSize: "0.9rem" }}>{displayName}</strong>
          <div style={{ marginTop: "0.25rem" }}>
            <span style={{
              padding: "0.25rem 0.5rem",
              backgroundColor: player.role === PlayerRole.AGENCY ? "#d4edda" : "#d1ecf1",
              color: player.role === PlayerRole.AGENCY ? "#155724" : "#0c5460",
              borderRadius: "4px",
              fontSize: "0.75rem",
              fontWeight: "bold"
            }}>
              {player.role}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <button
            onClick={() => setShowAdminMessageModal(true)}
            style={{
              padding: "0.25rem 0.75rem",
              fontSize: "0.75rem",
              cursor: "pointer",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              fontWeight: "500"
            }}
            title="Send admin message to this player"
          >
            Send Message
          </button>
        </div>
      </div>
      
      <div style={{ marginTop: "0.5rem", fontSize: "0.85rem", color: "#6c757d" }}>
        <div>
          <strong>Login Code:</strong> <span style={{ fontFamily: "monospace", fontWeight: "bold", color: "#212529" }}>{player.login_code}</span>
        </div>
        {actionCount !== null && (
          <div style={{ marginTop: "0.5rem" }}>
            <strong>Actions Made This Turn:</strong> <span style={{ fontWeight: "bold", color: "#212529" }}>{actionCount} / {MAX_ACTIONS_PER_TURN}</span>
          </div>
        )}
        <div style={{ marginTop: "0.5rem" }}>
          <strong>Characters Used This Turn:</strong> <span style={{ fontWeight: "bold", color: "#212529" }}>{charsSentThisTurn} / {MAX_CHARS_SENT_PER_TURN}</span>
        </div>
        <div style={{ marginTop: "0.5rem" }}>
          <AdminCounter
            label="Personal Score"
            value={personalScore}
            min={0}
            max={999}
            disabled={!isGameActive}
            onChangeRequested={handlePersonalScoreChange}
          />
        </div>
        {player.role === PlayerRole.AGENCY && (
          <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
            <AdminCounter
              label="Comm Level"
              value={commLevel ?? COMM_LEVEL_MIN}
              min={COMM_LEVEL_MIN}
              max={COMM_LEVEL_MAX}
              disabled={!isGameActive}
              onChangeRequested={handleCommChange}
            />
            <AdminCounter
              label="Scan Level"
              value={scanLevel ?? SCAN_LEVEL_MIN}
              min={SCAN_LEVEL_MIN}
              max={SCAN_LEVEL_MAX}
              disabled={!isGameActive}
              onChangeRequested={handleScanChange}
            />
          </div>
        )}
        {player.role === PlayerRole.AGENCY && (
          <div style={{ marginTop: "0.5rem" }}>
            <button
              onClick={() => setIsLandingSiteModalOpen(true)}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: "#6f42c1",
                color: "white",
                border: "none",
                borderRadius: "4px",
                fontSize: "0.85rem",
                fontWeight: "600",
                cursor: "pointer",
                width: "100%",
                transition: "background-color 0.2s"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#5a32a3";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#6f42c1";
              }}
            >
              Landing Site Points: {totalLandingSitePoints} / {MAX_LANDING_SITE_POINTS}
            </button>
          </div>
        )}
        {player.role === PlayerRole.SPACESHIP && (
          <>
            <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
              <AdminCounter
                label="Damage"
                value={damage}
                min={0}
                max={MAX_ALLOWED_SPACESHIP_DAMAGE}
                disabled={!isGameActive}
                onChangeRequested={handleDamageChange}
              />
              {isEliminated && (
                <div style={{
                  padding: "0.35rem 0.5rem",
                  backgroundColor: "#dc3545",
                  color: "white",
                  borderRadius: "4px",
                  fontSize: "0.8rem",
                  fontWeight: "bold"
                }}>
                  ELIMINATED
                </div>
              )}
            </div>
            <div style={{ marginTop: "0.5rem", display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <strong>Shields:</strong>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "0.25rem", cursor: isGameActive ? "pointer" : "not-allowed" }}>
                  <input
                    type="radio"
                    name={`shields-${player.player_id}`}
                    checked={shieldsOn === true}
                    onChange={() => handleShieldsToggle(true)}
                    disabled={!isGameActive}
                  />
                  On
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "0.25rem", cursor: isGameActive ? "pointer" : "not-allowed" }}>
                  <input
                    type="radio"
                    name={`shields-${player.player_id}`}
                    checked={shieldsOn === false}
                    onChange={() => handleShieldsToggle(false)}
                    disabled={!isGameActive}
                  />
                  Off
                </label>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Landing Site Points Modal */}
      {player.role === PlayerRole.AGENCY && (
        <ActionModal
          isOpen={isLandingSiteModalOpen}
          onClose={() => setIsLandingSiteModalOpen(false)}
          title="Landing Site Points"
        >
          <LandingSiteStatusContent
            playerId={player.player_id}
            investments={landingSiteInvestments}
            isLoading={isLoadingInvestments}
            onLoadInvestments={setLandingSiteInvestments}
            onSetLoading={setIsLoadingInvestments}
            onUpdateTotal={(total) => setTotalLandingSitePoints(total)}
          />
        </ActionModal>
      )}

      {/* Admin Message Modal */}
      <AdminMessageModal
        playerId={player.player_id}
        playerName={displayName}
        teamName={player.team_name}
        isOpen={showAdminMessageModal}
        onClose={() => setShowAdminMessageModal(false)}
        onSuccess={() => {
          // Optionally refresh data or show success message
          setShowAdminMessageModal(false);
        }}
      />
    </div>
  );
}

interface LandingSiteInvestment {
  player_id: string;
  player_name: string;
  team_name: string;
  count: number;
}

interface LandingSiteStatusContentProps {
  playerId: string;
  investments: LandingSiteInvestment[];
  isLoading: boolean;
  onLoadInvestments: (investments: LandingSiteInvestment[]) => void;
  onSetLoading: (loading: boolean) => void;
  onUpdateTotal?: (total: number) => void;
}

function LandingSiteStatusContent({ playerId, investments, isLoading, onLoadInvestments, onSetLoading, onUpdateTotal }: LandingSiteStatusContentProps) {
  // Use data passed from parent - no need to fetch again since parent already has it
  const totalInvestments = investments.reduce((sum, inv) => sum + inv.count, 0);

  return (
    <div style={{ padding: "1rem 0" }}>
      {isLoading ? (
        <div style={{ padding: "2rem", textAlign: "center", color: "#6c757d" }}>
          Loading landing site investments...
        </div>
      ) : (
        <>
          <div style={{ marginBottom: "1.5rem" }}>
            <h3 style={{ margin: "0 0 0.5rem 0", color: "#212529", fontSize: "1.1rem" }}>
              Total Landing Site Points: {totalInvestments}
            </h3>
            <p style={{ margin: 0, color: "#6c757d", fontSize: "0.9rem" }}>
              Number of players who have invested: {investments.length}
            </p>
          </div>
          <div style={{ borderTop: "1px solid #dee2e6", paddingTop: "1rem" }}>
            <h4 style={{ margin: "0 0 1rem 0", color: "#212529", fontSize: "1rem" }}>
              Investment Breakdown:
            </h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {investments.map((investment) => (
                <div
                  key={investment.player_id}
                  style={{
                    padding: "0.75rem 1rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px solid #dee2e6",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "1rem"
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "600", color: "#212529", marginBottom: "0.25rem" }}>
                      {investment.team_name}
                    </div>
                  </div>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem"
                  }}>
                    <div style={{
                      fontSize: "1.25rem",
                      fontWeight: "bold",
                      color: "#6f42c1",
                      minWidth: "3rem",
                      textAlign: "center"
                    }}>
                      {investment.count}
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const res = await authenticatedFetch(
                            `/players/landing-site-investment/add?target_player_id=${encodeURIComponent(playerId)}&investor_player_id=${encodeURIComponent(investment.player_id)}`,
                            { method: "POST" }
                          );
                          if (res.ok) {
                            // Use updated investment data from response instead of refetching
                            const data = await res.json();
                            if (data.investments && Array.isArray(data.investments)) {
                              onLoadInvestments(data.investments);
                              if (onUpdateTotal && typeof data.total_points === 'number') {
                                onUpdateTotal(data.total_points);
                              } else if (onUpdateTotal) {
                                // Fallback: calculate total from investments array
                                const total = data.investments.reduce((sum: number, inv: any) => sum + (inv.count || 0), 0);
                                onUpdateTotal(total);
                              }
                            } else {
                              // Fallback: If response doesn't include investments, refetch (backward compatibility)
                              console.warn("Investment response missing investments data, refetching");
                              const refreshRes = await fetch(`${API_BASE_URL}/players/landing-site-investments?player_id=${encodeURIComponent(playerId)}`);
                              if (refreshRes.ok) {
                                const refreshData = await refreshRes.json();
                                onLoadInvestments(refreshData);
                                if (onUpdateTotal) {
                                  const total = refreshData.reduce((sum: number, inv: any) => sum + inv.count, 0);
                                  onUpdateTotal(total);
                                }
                              }
                            }
                          } else {
                            const err = await res.json().catch(() => ({}));
                            alert(err.detail || "Failed to add investment");
                          }
                        } catch (error) {
                          console.error("Error adding investment:", error);
                          alert("Failed to add investment");
                        }
                      }}
                      disabled={totalInvestments >= MAX_LANDING_SITE_POINTS}
                      style={{
                        padding: "0.25rem 0.5rem",
                        backgroundColor: totalInvestments >= MAX_LANDING_SITE_POINTS ? "#6c757d" : "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: totalInvestments >= MAX_LANDING_SITE_POINTS ? "not-allowed" : "pointer",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        opacity: totalInvestments >= MAX_LANDING_SITE_POINTS ? 0.6 : 1
                      }}
                      title={totalInvestments >= MAX_LANDING_SITE_POINTS ? "Maximum landing site points reached" : "Add one point"}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

